import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { apiRequest } from '../utils/api';
import '../assets/styles/reports.css';

const metricOptions = [
  { id: 'newJoining', label: 'New Joining' },
  { id: 'relieving', label: 'Relieving' },
  { id: 'newPf', label: 'New PF' },
];

const EmployeeOverview = () => {
  const navigate = useNavigate();
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMetric, setSelectedMetric] = useState(metricOptions[0].id);
  const [metricsByYear, setMetricsByYear] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchOverview = useCallback(
    async (yearToLoad = null, updateSelection = false) => {
      try {
        setLoading(true);
        setError('');

        const params = new URLSearchParams();
        if (yearToLoad !== null && yearToLoad !== undefined && yearToLoad !== '') {
          params.set('year', yearToLoad);
        }
        const query = params.toString();
        const path = `/api/admin/reports/employee-overview${query ? `?${query}` : ''}`;

        const { data } = await apiRequest(path, {}, navigate);
        if (!data) {
          return;
        }

        if (Array.isArray(data.availableYears)) {
          setAvailableYears(data.availableYears.map((year) => String(year)));
        }

        if (data.metrics && data.year) {
          setMetricsByYear((prev) => ({
            ...prev,
            [String(data.year)]: data.metrics,
          }));
        }

        if (updateSelection && data.year) {
          setSelectedYear(String(data.year));
        }
      } catch (requestError) {
        setError(requestError.message || 'Unable to load employee overview data.');
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    fetchOverview(null, true);
  }, [fetchOverview]);

  const chartData = useMemo(() => {
    if (!selectedYear) {
      return [];
    }

    const metrics = metricsByYear[selectedYear];
    if (!metrics) {
      return [];
    }

    return metrics[selectedMetric] ?? [];
  }, [metricsByYear, selectedYear, selectedMetric]);

  const maxValue = useMemo(() => {
    if (!chartData.length) {
      return 0;
    }

    return Math.max(
      ...chartData.flatMap((entry) => [entry.male ?? 0, entry.female ?? 0]),
    );
  }, [chartData]);

  const totals = useMemo(() => {
    if (!chartData.length) {
      return { male: 0, female: 0 };
    }

    return chartData.reduce(
      (acc, entry) => ({
        male: acc.male + (entry.male ?? 0),
        female: acc.female + (entry.female ?? 0),
      }),
      { male: 0, female: 0 },
    );
  }, [chartData]);

  const totalEmployees = totals.male + totals.female;

  const handleYearChange = (event) => {
    const { value } = event.target;
    setSelectedYear(value);

    if (!metricsByYear[value]) {
      const numericYear = Number.parseInt(value, 10);
      fetchOverview(Number.isNaN(numericYear) ? value : numericYear, false);
    }
  };

  return (
    <DashboardLayout title="Employee Overview">
      <div className="employee-overview-wrapper">
        <div className="employee-overview-card">
          <div className="overview-card-header">
            <div>
              <span className="overview-subtitle">Report</span>
              <h2 className="overview-title">Employee Overview</h2>
            </div>
          </div>

          {loading && !error && (
            <div className="overview-status" role="status">
              Loading employee overview data...
            </div>
          )}
          {error && (
            <div className="overview-status overview-status-error" role="alert">
              {error}
            </div>
          )}

          <div className="overview-summary">
            <div>
              <p className="overview-summary-label">
                Total {metricOptions.find((option) => option.id === selectedMetric)?.label}
              </p>
              <p className="overview-summary-value">{totalEmployees.toLocaleString()}</p>
            </div>
            <div className="overview-summary-split">
              <div>
                <span className="male-dot" aria-hidden="true" />
                <span className="overview-summary-caption">Male</span>
                <span className="overview-summary-number">{totals.male.toLocaleString()}</span>
              </div>
              <div>
                <span className="female-dot" aria-hidden="true" />
                <span className="overview-summary-caption">Female</span>
                <span className="overview-summary-number">{totals.female.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="overview-controls">
            <div className="overview-tabs" role="tablist" aria-label="Employee metric">
              {metricOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={[
                    'overview-tab-button',
                    selectedMetric === option.id ? 'overview-tab-button-active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setSelectedMetric(option.id)}
                  aria-pressed={selectedMetric === option.id}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="overview-year-filter">
              <label htmlFor="employee-overview-year">Select the Year:</label>
              <div className="overview-select-wrapper">
                <select
                  id="employee-overview-year"
                  value={selectedYear}
                  onChange={handleYearChange}
                  disabled={!availableYears.length}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="overview-chart-card">
            <div className="overview-chart-header">
              <div className="overview-chart-legend" aria-label="Gender legend">
                <span>
                  <span className="male-dot" aria-hidden="true" /> Male
                </span>
                <span>
                  <span className="female-dot" aria-hidden="true" /> Female
                </span>
              </div>
              <p className="overview-chart-note">
                Comparing headcount by gender for {selectedYear || 'selected year'}.
              </p>
            </div>
            <div className="overview-chart">
              {chartData.length === 0 ? (
                <div className="overview-empty-state">No data available for the selected filters.</div>
              ) : (
                chartData.map((entry) => {
                  const maleValue = entry.male ?? 0;
                  const femaleValue = entry.female ?? 0;
                  const maleHeight = maxValue ? Math.round((maleValue / maxValue) * 100) : 0;
                  const femaleHeight = maxValue ? Math.round((femaleValue / maxValue) * 100) : 0;

                  return (
                    <div key={`${entry.month}-${selectedMetric}`} className="overview-chart-column">
                      <div className="overview-chart-bars">
                        <div className="overview-bar-wrapper">
                          <div
                            className="overview-bar male-bar"
                            style={{ height: `${maleHeight}%` }}
                          >
                            <span>{maleValue}</span>
                          </div>
                        </div>
                        <div className="overview-bar-wrapper">
                          <div
                            className="overview-bar female-bar"
                            style={{ height: `${femaleHeight}%` }}
                          >
                            <span>{femaleValue}</span>
                          </div>
                        </div>
                      </div>
                      <div className="overview-month-label">{entry.month}</div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmployeeOverview;
